import React, { useState } from "react";
import { auth } from "../firebase";

const COLORS = {
  blue: "#2563EB",
  slate: "#94A3B8",
  dark: "#020617",
  card: "#0B1220",
  border: "rgba(148,163,184,0.24)",
};

export default function MfaVerify({ onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const cleanCode = code.trim();

    if (cleanCode.length !== 6) {
      setError("Please enter your 6-digit verification code.");
      return;
    }

    try {
      setLoading(true);

      const currentUser = auth.currentUser;

      if (!currentUser) {
        setError("Your session expired. Please sign in again.");
        return;
      }

      const idToken = await currentUser.getIdToken(true);

      const response = await fetch(
        "https://us-central1-my-firstproject-350605-a54a7.cloudfunctions.net/verifyMfaCode",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ token: cleanCode }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        setError(result.error || "Invalid verification code. Please try again.");
        return;
      }

      onSuccess?.();
    } catch (err) {
      console.error("MFA verification failed:", err);
      setError(err?.message || "Unable to verify your code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.glowOne} />
        <div style={styles.glowTwo} />

        <div style={styles.badge}>Secure Access</div>

        <div style={styles.brandRow}>
          <img src="/logo-icon.png" alt="Tanvoracloud" style={styles.logo} />

          <div>
            <h1 style={styles.title}>
              <span style={styles.titleWhite}>TanSmartX</span>{" "}
              <span style={styles.titleGradient}>FinOps</span>
            </h1>
            <p style={styles.company}>by Tanvoracloud</p>
          </div>
        </div>

        <p style={styles.description}>
          Secure your access to the{" "}
          <strong style={styles.strong}>Cloud Financial Control Tower</strong>.
          Enter your 6-digit verification code from your authenticator app to
          continue.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            value={code}
            onChange={(event) =>
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="Enter your 6-digit verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            style={styles.input}
          />

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="submit"
            disabled={loading}
            style={loading ? styles.buttonDisabled : styles.button}
          >
            {loading ? "Verifying..." : "Verify Code"}
          </button>
        </form>

        <p style={styles.helper}>
          Use Google Authenticator or Microsoft Authenticator. Having trouble
          signing in? Please contact your system administrator.
        </p>

        <div style={styles.trustLine}>
          Protected by enterprise-grade multi-factor authentication
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(37,99,235,0.16), transparent 28%), radial-gradient(circle at top right, rgba(14,165,233,0.10), transparent 30%), #020617",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    color: "white",
    fontFamily: "Inter, Arial, sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "560px",
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(3,7,18,0.98))",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "26px",
    padding: "42px 48px",
    boxShadow: "0 24px 70px rgba(0,0,0,0.42)",
  },

  glowOne: {
    position: "absolute",
    top: "-90px",
    right: "-90px",
    width: "190px",
    height: "190px",
    background: "radial-gradient(circle, rgba(37,99,235,0.22), transparent 68%)",
  },

  glowTwo: {
    position: "absolute",
    bottom: "-110px",
    left: "-110px",
    width: "220px",
    height: "220px",
    background: "radial-gradient(circle, rgba(14,165,233,0.14), transparent 70%)",
  },

  badge: {
    display: "inline-flex",
    padding: "8px 16px",
    borderRadius: "999px",
    background: "rgba(37,99,235,0.22)",
    border: "1px solid rgba(37,99,235,0.35)",
    color: "#BFDBFE",
    fontSize: "13px",
    fontWeight: 900,
    marginBottom: "30px",
    position: "relative",
    zIndex: 1,
  },

  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
    marginBottom: "24px",
    position: "relative",
    zIndex: 1,
  },

  logo: {
    height: "58px",
    width: "auto",
    objectFit: "contain",
    display: "block",
    filter:
      "brightness(1.25) contrast(1.15) drop-shadow(0 0 18px rgba(14,165,233,0.75))",
  },

  title: {
    margin: 0,
    fontSize: "30px",
    lineHeight: 1.1,
    letterSpacing: "-0.8px",
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  titleWhite: {
    color: "#FFFFFF",
  },

  titleGradient: {
    background: "linear-gradient(90deg, #67E8F9, #22D3EE, #38BDF8)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textShadow: "0 0 25px rgba(56,189,248,0.45)",
  },

  company: {
    margin: "7px 0 0",
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: 800,
  },

  description: {
    margin: "0 0 28px",
    color: "#CBD5E1",
    fontSize: "16px",
    lineHeight: 1.75,
    position: "relative",
    zIndex: 1,
  },

  strong: {
    color: "#BFDBFE",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    background: "rgba(2,6,23,0.78)",
    border: "1px solid rgba(148,163,184,0.32)",
    color: "white",
    borderRadius: "18px",
    padding: "22px 20px",
    textAlign: "center",
    fontSize: "22px",
    letterSpacing: "3px",
    outline: "none",
    marginBottom: "16px",
  },

  error: {
    color: "#FCA5A5",
    background: "rgba(220,38,38,0.10)",
    border: "1px solid rgba(220,38,38,0.25)",
    borderRadius: "14px",
    padding: "12px",
    marginBottom: "14px",
    fontSize: "14px",
    fontWeight: 800,
  },

  button: {
    width: "100%",
    border: "none",
    borderRadius: "18px",
    padding: "19px 22px",
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "white",
    fontSize: "18px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 18px 36px rgba(37,99,235,0.28)",
  },

  buttonDisabled: {
    width: "100%",
    border: "none",
    borderRadius: "18px",
    padding: "19px 22px",
    background: "#334155",
    color: "#94A3B8",
    fontSize: "18px",
    fontWeight: 950,
    cursor: "not-allowed",
  },

  helper: {
    color: "#94A3B8",
    fontSize: "14px",
    lineHeight: 1.6,
    textAlign: "center",
    margin: "24px 0 0",
  },

  trustLine: {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid rgba(148,163,184,0.16)",
    color: "#64748B",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    textAlign: "center",
  },
};